/**
 * Vision Document-Classification settings card.
 *
 * Owner-only card under Settings → Workflow & UI. Three controls:
 *   1. Master toggle — enable/disable the per-upload classifier
 *   2. Confidence threshold slider — anything below goes to the queue
 *   3. Open-queue link with a live count badge
 *
 * Backed by:
 *   GET  /api/document-review-queue/settings
 *   POST /api/document-review-queue/settings
 *
 * Design choice: this card is intentionally NOT a modal. The user
 * adjusts threshold inline, the FE writes through on each commit, and
 * the badge re-fetches once a minute (cheap call).
 */
import { useCallback, useEffect, useState } from "react";
import { Eye, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useBranding } from "../../context/TenantBrandingContext";

export default function VisionClassifyCard() {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [threshold, setThreshold] = useState(0.6);
  const [queueCount, setQueueCount] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/document-review-queue/settings");
      setEnabled(!!r.data.vision_doc_classify_enabled);
      setThreshold(Number(r.data.threshold ?? 0.6));
      setQueueCount(Number(r.data.open_queue_count ?? 0));
    } catch {
      // Silent — card just shows its defaults; backend may be older.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const tick = setInterval(load, 60_000);
    return () => clearInterval(tick);
  }, [load]);

  const persist = useCallback(async (patch) => {
    setSaving(true);
    try {
      await api.post("/document-review-queue/settings", patch);
      await load();
    } catch (e) {
      console.warn("vision settings save failed", e);
    } finally {
      setSaving(false);
    }
  }, [load]);

  if (loading) {
    return (
      <div className="hv-card p-5">
        <div className="text-xs text-[#647087]">Loading vision settings…</div>
      </div>
    );
  }

  return (
    <div className="hv-card p-5" data-testid="vision-classify-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-[#1fb6c1]/15 border border-[#1fb6c1]/40 flex items-center justify-center shrink-0">
          <Eye className="w-5 h-5 text-[#1fb6c1]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-bold">Vision document check</h2>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#1fb6c1]/80 border border-[#1fb6c1]/40 px-1.5 py-0.5 rounded">
              <Sparkles className="w-3 h-3" />
              {aiName} Vision
            </span>
          </div>
          <p className="text-sm text-[#a0aabf] max-w-prose mt-1">
            {aiName} takes a quick second look at every uploaded document (receipts,
            quotes, invoices, CVs) and flags anything it&apos;s not sure about.
            Low-confidence uploads land in a review queue so a wrong-categorised
            file never silently slips into your books.
          </p>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Toggle vision document check"
          onClick={() => persist({ vision_doc_classify_enabled: !enabled })}
          disabled={saving}
          className={`relative w-12 h-6 rounded-full transition shrink-0 ${
            enabled ? "bg-[#1fb6c1]" : "bg-[#2a3142]"
          } disabled:opacity-50`}
          data-testid="vision-classify-toggle"
        >
          <span
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
              enabled ? "left-[26px]" : "left-0.5"
            }`}
          />
        </button>
      </div>

      <div className={`mt-5 ${enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <label className="text-xs uppercase tracking-[0.2em] text-[#647087] block">
          Confidence threshold
          <span className="ml-2 font-mono normal-case tracking-normal text-[#1fb6c1]">
            {Math.round(threshold * 100)}%
          </span>
        </label>
        <input
          type="range"
          min={0.3}
          max={0.9}
          step={0.05}
          value={threshold}
          disabled={!enabled || saving}
          onChange={(e) => setThreshold(Number(e.target.value))}
          onMouseUp={(e) => persist({ threshold: Number(e.target.value) })}
          onTouchEnd={(e) => persist({ threshold: Number(e.target.value) })}
          className="w-full mt-2 accent-[#1fb6c1]"
          data-testid="vision-classify-threshold"
        />
        <div className="text-[11px] text-[#647087] mt-1 flex justify-between">
          <span>Quiet queue (catches only obvious confusion)</span>
          <span>Strict queue (catches anything Hidro isn&apos;t sure about)</span>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-[#2a3142]">
        <Link
          to="/settings/document-review-queue"
          className="inline-flex items-center justify-between gap-3 w-full hv-btn hv-btn-ghost text-sm"
          data-testid="vision-classify-queue-link"
        >
          <span className="flex items-center gap-2">
            Review queue
            {queueCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[11px] font-semibold"
                data-testid="vision-classify-queue-count"
              >
                {queueCount}
              </span>
            )}
          </span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
